/**
 * Modo de renderização do navegador.
 *
 * Sem aceleração de hardware, toda animação contínua obriga a CPU a recompor a
 * tela inteira a cada quadro. O shell marca `<html data-render="software">`
 * uma vez, na carga, e o CSS usa isso para aquietar só o que é enfeite em
 * movimento constante — nada de informação, layout ou cor muda.
 */
let softwareCache: boolean | null = null;

/**
 * Detecta renderização por software. `failIfMajorPerformanceCaveat` faz o
 * navegador recusar o WebGL quando não há GPU de verdade; quando ele aceita, o
 * nome do renderizador ainda denuncia os rasterizadores de software.
 */
export function isSoftwareRendering(): boolean {
  if (softwareCache !== null) return softwareCache;
  softwareCache = false;
  try {
    const probe = document.createElement("canvas");
    const gl = probe.getContext("webgl", { failIfMajorPerformanceCaveat: true }) as WebGLRenderingContext | null;
    if (!gl) {
      // Recusou com a ressalva: ou há WebGL só por software, ou não há WebGL
      // nenhum — o caso do Chrome com a aceleração desligada. Nos dois, o
      // navegador desenha na CPU.
      softwareCache = true;
      return softwareCache;
    }
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) : "";
    softwareCache = /swiftshader|llvmpipe|software|basic render/i.test(renderer);
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    softwareCache = false;
  }
  return softwareCache;
}

/** Marca o documento para o CSS. Chamado uma vez, antes do primeiro render. */
export function markRenderMode(root: HTMLElement = document.documentElement): void {
  root.dataset.render = isSoftwareRendering() ? "software" : "gpu";
}
